// ── IS A WHOLE INSURANCE UNIT RUNNING BELOW ITS GUARANTEE? (machine yield, agents only) ─
//
// Kevin, 2026-10-31: *"We should flag only if a farmers yield runs below insurance guarantee
// across their entire unit (generally county wide)."* Not a field. A poor field inside a unit
// that made its guarantee is not a loss — the unit settles as a whole — so a field-level flag
// would send somebody chasing claims the policy cannot pay.
//
// ── WHAT A UNIT IS HERE, BY `crop_coverage_units.unit_structure` ─────────────
// Measured over CY2026 base-plan rows carrying acres (2026-10-31): every one states a
// structure, EU (enterprise) by far the most, then BU (basic) and OU (optional). Inside an
// EU or a BU the `sub_unit` numbers are the unit's APH databases (one per section / FSN), not
// separate insurance units — Blair corn is EU 0001 with databases 0001, 0003, 0004, 0007 at
// four different approved yields — so a loss is decided over the unit NUMBER, all databases
// together. Only an OU makes each unit-number + sub-unit its own unit.
//   EU, BU   one unit per (policy, crop, unit number)
//   OU       one unit per (policy, crop, unit number, sub unit)
//   anything else (UA, blank, or two structures on one unit number)  -> not checked, named
//
// ── THE GUARANTEE, IN PRODUCTION TERMS ───────────────────────────────────────
// Σ over the unit's PLANTED insured acres of the per-acre guarantee x acres, divided by those
// acres, so units with several databases at different APHs weigh each one by its own acres.
// The per-acre guarantee is the same ladder App.jsx `statedGuaranteeBu` uses for the bushel
// guarantee: RMA's own Acre Guarantee Quantity (P11 #120, `acre_guarantee_qty`, NAU) where it
// is stated; otherwise approved yield x coverage level, ONLY on a timely unit (no
// `acreage_status`) — a late-planted or capped row carries a factor that is not in the file,
// so it is not derivable and the unit is not checked. Prevent-plant rows are left out: they
// carry their own guarantee and grow no crop. SCO/ECO rows restate the same acres and are
// skipped by `unitPlanClass` (productionUnits.js), the one rule for that.
//
// ── THE PRODUCTION TO COMPARE, FROM THE MACHINE ──────────────────────────────
// `jd_field_ftf` yield rows (already filtered to evidence by src/claims/jdEvidence.js — gone
// and duplicate passes out) on the unit's insured CLUs, this crop year, this crop. Two of the
// claims code's own rules apply unchanged:
//   - ONLY A COMBINED PASS IS GRAIN (src/claims/harvestForm.js). A chopped pass on a grain
//     unit is silage tonnage, never bushels; it does not count toward the unit's harvested
//     acres, so a grain unit that was partly chopped reads "not enough harvested" and the
//     appraisal is what settles those acres. Counted on the row, never silent.
//   - A FIELD AN AGENT MARKED HIGH-MOISTURE CORN (claim_hmc_fields, fieldCoverage.js
//     `dropHmcRows`) has its combine rows dropped, as on the claim panel.
// ⚠ CORN SILAGE UNITS ARE NOT CHECKED. Insured silage (type 026) is stated in tons, and the
// machine's silage figure is not recorded on a stated basis in `jd_field_ftf` (api/_jd.js:
// a chopper's VRYIELDMAS column is DRY MATTER, and 106 older silage grids carry no column at
// all). Comparing either to a wet-ton guarantee would invent a loss or hide one. A unit that
// carries silage acres, or more than one type, is therefore named and not compared — the
// same refusal productionEvidence.js makes for a crop carrying two units of measure.
//
// ── POST-CALIBRATION, THE SAME FACTOR THE MACHINE DATA PAGE SHOWS ───────────
// An uncalibrated monitor is wrong by a farm-wide multiple (Kennedy's 2026 wheat read 46.4 on
// a crop that made ~71). Where an agent or grower entered the crop's true total
// (`yield_calibrations`), the factor is api/_jd.js hFieldData's: (total bushels / acres) /
// monitor yield, acres = the entered acres, else the reported (policy_fields) acres, else the
// monitor's own acres, rounded to 0.001. With no calibration the monitor figure is used AS
// RECORDED and the row says so, because that is the case most likely to read short.
//
// ── WHEN THERE IS ENOUGH HARVESTED TO SAY ANYTHING ───────────────────────────
// At least 90% of the unit's planted insured acres must carry a combine yield — Kevin's floor
// for the production report (api/_production-fill.js MIN_MACHINE_COVERAGE, 2026-10-18), reused
// so the two screens cannot disagree about when the machine has seen a unit. Below it the unit
// reads "not enough harvested yet". Over 102% of its acres (productionUnits.js OVER_COVER_TOL)
// the machine ran ground the unit does not insure and the unit is not checked.
// The comparison is YIELD on the mapped acres against the acre-weighted guarantee, which is the
// unit's production projected to all its planted acres — so the 4% not yet mapped neither fakes
// a shortfall nor hides one.
//
// ⚠ NO DOLLARS, AND NOT A CLAIM ESTIMATE. A production to count also carries appraisals,
// uninsured causes and quality, none of which the machine knows. This says "the machine's
// yield on this unit is under its guarantee" and stops there. Agents only.
"use strict";

const { unitPlanClass } = require("./productionUnits.js");
const { harvestFormOf } = require("./harvestForm.js");

const RULE_VERSION = "unit-guarantee-v1";

// Kevin's 90% (see the header). tools/check-unit-guarantee-flag.js asserts it equals
// api/_production-fill.js MIN_MACHINE_COVERAGE, so changing one without the other fails CI.
const MIN_MAPPED_SHARE = 0.9;
// productionUnits.js OVER_COVER_TOL — the same 2% productionEvidence allows.
const OVER_COVER_TOL = 1.02;

const STATUS = Object.freeze({
  BELOW: "below",               // whole unit's machine yield is under its guarantee
  AT_OR_ABOVE: "at_or_above",   // checked, and it is not
  NOT_ENOUGH: "not_enough",     // under 90% of the unit's acres carry a combine yield
  NOT_CHECKED: "not_checked",   // the comparison cannot be made honestly; `why` says why
});

const n = (v) => {
  if (v == null || v === "") return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
const pad4 = (v) => String(v == null ? "" : v).trim().padStart(4, "0");
// "Type not stated" has three spellings (ppCells.js): 997 / 000 / blank.
const typeKey = (t) => { const v = up(t); return (v === "997" || v === "000") ? "" : v; };
// Corn type 026 / GSS is silage (claimCrops.js isSilageType — that module is ESM, and this one
// is loaded by api/ too, so the four spellings are read here rather than imported).
const isSilageType = (t) => ["026", "GSS", "SILAGE", "SILAGE-026"].indexOf(up(t)) >= 0;
const yearOf = (d) => (d ? Number(String(d).slice(0, 4)) : null);

// ── THE UNIT A MACHINE YIELD CAN BE STATED IN ────────────────────────────────
// Combine data is bushels per acre for the grains; dry beans are kept in POUNDS by api/_jd.js
// (INSURED_IN_LB) because that is what they are insured in. Every other crop (potatoes, grapes,
// cabbage, processing beans) is measured in a unit no combine records, so it is not checked
// rather than compared in a unit nobody established. claimCrops.js claimUnit agrees on all of
// these; it is not imported for the reason above.
const BUSHEL_CROPS = new Set(["CORN", "SOYBEANS", "WHEAT", "WINTER WHEAT", "BARLEY", "OATS",
  "TRITICALE", "RYE", "GRAIN SORGHUM"]);
const POUND_CROPS = new Set(["DRY BEANS"]);
function machineUnitFor(crop) {
  const c = up(crop);
  if (BUSHEL_CROPS.has(c)) return "bu/ac";
  if (POUND_CROPS.has(c)) return "lb/ac";
  return null;
}

// Lower-case plain crop word for an agent sentence ("corn", "dry beans").
const cropWord = (crop) => String(crop || "").trim().toLowerCase();

// ── POST-CALIBRATION FACTOR (api/_jd.js hFieldData, same arithmetic) ─────────
// calibrations   yield_calibrations rows for ONE farmer
// policyFields   that farmer's policy_fields rows (the reported acres)
// jdFtf          that farmer's evidence-grade jd_field_ftf rows
// Returns { factor, ... } or null. ⚠ Two totals on file for one crop and year is refused
// (null + `ambiguous`): hFieldData keeps whichever row it read last, and which that is depends
// on the order Postgres returns them — a factor that can flip between loads is not one.
function calibrationFor({ crop, cropYear, calibrations = [], policyFields = [], jdFtf = [] }) {
  const CROP = up(crop), CY = Number(cropYear);
  const rows = (calibrations || []).filter((c) => up(c.crop) === CROP && Number(c.crop_year) === CY && n(c.total_bushels) > 0);
  if (!rows.length) return null;
  if (rows.length > 1) return { factor: null, ambiguous: true, rows: rows.length };
  const c = rows[0];
  const repAc = (policyFields || []).filter((p) => up(p.crop) === CROP && Number(p.crop_year) === CY)
    .reduce((s, p) => s + (n(p.acres) || 0), 0);
  const mon = (jdFtf || []).filter((r) => r.metric === "yield" && up(r.crop) === CROP && yearOf(r.operation_date) === CY
    && n(r.mean_value) > 0 && n(r.acres) > 0);
  const monAc = mon.reduce((s, r) => s + n(r.acres), 0);
  const monBu = mon.reduce((s, r) => s + n(r.mean_value) * n(r.acres), 0);
  const denomAc = n(c.total_acres) || repAc || monAc;
  if (!(denomAc > 0) || !(monAc > 0) || !(monBu > 0)) return null;
  const target = n(c.total_bushels) / denomAc, monY = monBu / monAc;
  if (!(target > 0) || !(monY > 0)) return null;
  return { factor: Math.round((target / monY) * 1000) / 1000, totalBushels: n(c.total_bushels),
    targetYield: Math.round(target * 10) / 10, monitorYield: Math.round(monY * 10) / 10, source: c.source || null };
}

// ── THE GUARANTEE PER ACRE ON ONE PLANTED UNIT ROW ───────────────────────────
// Returns { perAcre, basis } or { perAcre: null, why }. App.jsx statedGuaranteeBu's ladder.
function rowGuaranteePerAcre(u) {
  const q = n(u.acre_guarantee_qty);
  if (q > 0) return { perAcre: q, basis: "stated" };
  if (u.acreage_status) {
    return { perAcre: null, why: `part of this unit carries a guarantee adjustment (${String(u.acreage_status)}) and the carrier did not state the adjusted guarantee` };
  }
  const aph = n(u.aph_yield), cov = n(u.coverage_level);
  if (aph > 0 && cov > 0) return { perAcre: aph * cov, basis: "aph_x_coverage" };
  return { perAcre: null, why: "part of this unit has no approved yield or coverage level on file" };
}

// ── THE INSURANCE UNITS OF A CROP YEAR, from the carrier's unit rows ─────────────────────
// Shared by this flag and the Claims Manager's yield-per-unit table (unitYield.js), so the two
// screens read one set of units with one guarantee each. `policyIds` is anything with .has().
// Base plan only (ECO/SCO bands sit on top of a unit, they are not one). An optional unit is
// its own row per sub-unit; enterprise and basic units are one row per unit number.
function groupCoverageUnits({ cropYear, policyIds, coverageUnits = [] } = {}) {
  const CY = Number(cropYear);
  const byNumber = new Map();
  for (const u of coverageUnits || []) {
    if (!policyIds.has(u.policy_id)) continue;
    if (u.crop_year != null && Number(u.crop_year) !== CY) continue;
    if (unitPlanClass(u.plan) !== "base") continue;
    if (!String(u.unit_number || "").trim()) continue;
    const k = `${u.policy_id}|${up(u.crop)}|${pad4(u.unit_number)}`;
    if (!byNumber.has(k)) byNumber.set(k, []);
    byNumber.get(k).push(u);
  }
  const unitGroups = [];
  for (const [k, rows] of byNumber) {
    // A BLANK structure is "not stated", not a second structure. Measured CY2026: 43 unit
    // numbers carry blank-structure rows beside their EU/BU rows, and not one blank-structure
    // base row carries acres. Only the rows that STATE a structure decide it; two different
    // stated ones refuse the unit.
    const structures = new Set(rows.map((r) => up(r.unit_structure)).filter(Boolean));
    const s = structures.size === 1 ? [...structures][0] : null;
    if (s === "OU") {
      const bySub = new Map();
      for (const r of rows) { const sk = pad4(r.sub_unit); if (!bySub.has(sk)) bySub.set(sk, []); bySub.get(sk).push(r); }
      for (const [sub, sr] of bySub) unitGroups.push({ key: `${k}|${sub}`, structure: "OU", subUnit: sub, rows: sr });
    } else {
      unitGroups.push({ key: k, structure: s === "EU" || s === "BU" ? s : null,
        structureSeen: [...structures].map((x) => x || "(blank)"), subUnit: null, rows });
    }
  }
  return unitGroups;
}

// THE GUARANTEE PER ACRE OF ONE UNIT: each planted row's own (rowGuaranteePerAcre), acre-weighted.
// { perAcre, basis } or { perAcre: null, why } — one row the carrier left without a guarantee
// leaves the whole unit without one rather than averaging over the rows that have it.
function unitGuaranteePerAcre(plantedRows) {
  let gQty = 0, gAc = 0, stated = 0, derived = 0;
  for (const r of plantedRows || []) {
    const gr = rowGuaranteePerAcre(r);
    if (gr.perAcre == null) return { perAcre: null, why: gr.why };
    gQty += gr.perAcre * n(r.acres); gAc += n(r.acres);
    if (gr.basis === "stated") stated++; else derived++;
  }
  if (!(gAc > 0)) return { perAcre: null, why: "the unit has no planted acres to carry a guarantee" };
  return { perAcre: +(gQty / gAc).toFixed(1), basis: derived === 0 ? "stated" : stated === 0 ? "aph_x_coverage" : "mixed" };
}

/**
 * buildUnitGuaranteeFlags({ cropYear, policies, coverageUnits, policyFields, jdFtf, calibrations, hmcFields })
 *
 *   policies       [{ id, farmer_id, policy_number }] — the crop year's policies to check
 *   coverageUnits  crop_coverage_units rows (UNIT_GUARANTEE_NEEDS lists the columns)
 *   policyFields   policy_fields rows for the same policies and year
 *   jdFtf          jd_field_ftf yield rows, ALREADY evidence-filtered (jdEvidence.js)
 *   calibrations   yield_calibrations rows
 *   hmcFields      claim_hmc_fields rows
 *
 * Returns { units: [...], counts }. One row per insurance unit with a planted crop on it.
 */
function buildUnitGuaranteeFlags({
  cropYear, policies = [], coverageUnits = [], policyFields = [], jdFtf = [], calibrations = [], hmcFields = [],
} = {}) {
  const CY = Number(cropYear);
  const pol = new Map((policies || []).map((p) => [p.id, p]));

  // ── 1. the insurance units, from the carrier's unit rows ─────────────────
  const unitGroups = groupCoverageUnits({ cropYear: CY, policyIds: pol, coverageUnits });

  // ── 2. the insured fields of each unit ───────────────────────────────────
  const fieldsByUnit = new Map();
  const noUnitFields = new Map();   // policy|CROP -> count of fields with no unit
  for (const p of policyFields || []) {
    if (!pol.has(p.policy_id) || Number(p.crop_year) !== CY) continue;
    const pc = `${p.policy_id}|${up(p.crop)}`;
    if (!String(p.unit_number || "").trim()) { noUnitFields.set(pc, (noUnitFields.get(pc) || 0) + 1); continue; }
    for (const key of [`${pc}|${pad4(p.unit_number)}`, `${pc}|${pad4(p.unit_number)}|${pad4(p.sub_unit)}`]) {
      if (!fieldsByUnit.has(key)) fieldsByUnit.set(key, []);
      fieldsByUnit.get(key).push(p);
    }
  }

  // ── 3. machine yield per (farmer, crop, CLU), combined passes only ───────
  const hmc = new Set((hmcFields || []).filter((h) => Number(h.crop_year) === CY)
    .map((h) => `${h.farmer_id}|${up(h.crop)}|${up(h.field_key)}`));
  const machine = new Map();
  for (const r of jdFtf || []) {
    if (r.metric !== "yield" || yearOf(r.operation_date) !== CY) continue;
    const ac = n(r.acres), mv = n(r.mean_value), clu = up(r.clu_id);
    if (!(ac > 0) || !(mv > 0) || !clu) continue;
    const form = harvestFormOf(r);
    // A chopped pass is filed under the crop it is insured as (CORN SILAGE -> CORN), counted as
    // chopped, and never summed as grain.
    const insuredCrop = up(r.crop) === "CORN SILAGE" ? "CORN" : up(r.crop);
    const k = `${r.farmer_id}|${insuredCrop}|${clu}`;
    const e = machine.get(k) || { acres: 0, qty: 0, choppedAcres: 0, otherFormAcres: 0, hmcAcres: 0 };
    if (form === "chopped") e.choppedAcres += ac;
    else if (form !== "combined") e.otherFormAcres += ac;
    else if (hmc.has(`${r.farmer_id}|${insuredCrop}|${clu}`)) e.hmcAcres += ac;
    else { e.acres += ac; e.qty += ac * mv; }
    machine.set(k, e);
  }
  // One calibration per (farmer, crop), computed off the farmer's own rows.
  const calCache = new Map();
  const calOf = (farmerId, crop) => {
    const ck = `${farmerId}|${up(crop)}`;
    if (!calCache.has(ck)) {
      calCache.set(ck, calibrationFor({ crop, cropYear: CY,
        calibrations: (calibrations || []).filter((c) => c.farmer_id === farmerId),
        policyFields: (policyFields || []).filter((p) => p.farmer_id === farmerId || (pol.get(p.policy_id) || {}).farmer_id === farmerId),
        jdFtf: (jdFtf || []).filter((r) => r.farmer_id === farmerId) }));
    }
    return calCache.get(ck);
  };

  // ── 4. one row per unit ──────────────────────────────────────────────────
  const units = [];
  for (const g of unitGroups) {
    const first = g.rows[0];
    const policy = pol.get(first.policy_id);
    const crop = up(first.crop);
    const planted = g.rows.filter((r) => !r.is_prevent_plant && n(r.acres) > 0);
    const ppAcres = g.rows.filter((r) => r.is_prevent_plant).reduce((s, r) => s + (n(r.acres) || 0), 0);
    if (!planted.length) continue;   // nothing planted: no production to compare (PP has its own screen)
    const counties = [...new Set(planted.map((r) => up(r.county)).filter(Boolean))];
    const unitLabel = g.structure === "OU" ? `${pad4(first.unit_number)}-${g.subUnit}` : pad4(first.unit_number);
    const row = {
      key: g.key, policyId: first.policy_id, farmerId: policy ? policy.farmer_id : null,
      policyNumber: policy ? policy.policy_number || null : null,
      crop, county: counties.join(" / ") || null, unit: unitLabel, structure: g.structure,
      databases: new Set(g.rows.map((r) => pad4(r.sub_unit))).size,
      plantedAcres: +planted.reduce((s, r) => s + n(r.acres), 0).toFixed(2), ppAcres: +ppAcres.toFixed(2),
      yieldUnit: machineUnitFor(crop),
      guaranteeYield: null, guaranteeBasis: null, machineYield: null, machineYieldRaw: null,
      mappedAcres: 0, mappedShare: null, mappedPct: null, choppedAcres: 0, otherFormAcres: 0, hmcAcres: 0,
      fields: 0, fieldsWithYield: 0, fieldsNoUnitOnPolicy: noUnitFields.get(`${first.policy_id}|${crop}`) || 0,
      calibration: null, status: STATUS.NOT_CHECKED, why: null,
    };
    units.push(row);

    if (!g.structure) {
      row.why = `unit structure ${(g.structureSeen || ["not stated"]).join(" and ")} is not one this check reads (enterprise, basic or optional)`;
      continue;
    }
    const types = new Set(planted.map((r) => typeKey(r.type_code)));
    if (planted.some((r) => isSilageType(r.type_code))) {
      row.why = "corn silage is insured in tons and the machine's silage figure has no stated wet-ton basis, so it is not compared";
      continue;
    }
    if (types.size > 1) { row.why = `this unit carries more than one type (${[...types].map((t) => t || "not stated").join(", ")}), which are not compared as one figure`; continue; }
    if (!row.yieldUnit) { row.why = `${cropWord(crop)} is not measured by a combine yield monitor`; continue; }

    // the guarantee
    const ug = unitGuaranteePerAcre(planted);
    if (ug.perAcre == null) { row.why = ug.why; continue; }
    row.guaranteeYield = ug.perAcre;
    row.guaranteeBasis = ug.basis;

    // the machine
    const fieldKey = g.structure === "OU" ? `${first.policy_id}|${crop}|${pad4(first.unit_number)}|${g.subUnit}`
      : `${first.policy_id}|${crop}|${pad4(first.unit_number)}`;
    const fields = fieldsByUnit.get(fieldKey) || [];
    const clus = new Set(fields.map((f) => up(f.clu_id)).filter(Boolean));
    row.fields = clus.size;
    let mAc = 0, mQty = 0;
    for (const clu of clus) {
      const m = machine.get(`${row.farmerId}|${crop}|${clu}`);
      if (!m) continue;
      mAc += m.acres; mQty += m.qty;
      row.choppedAcres += m.choppedAcres; row.otherFormAcres += m.otherFormAcres; row.hmcAcres += m.hmcAcres;
      if (m.acres > 0) row.fieldsWithYield++;
    }
    row.choppedAcres = +row.choppedAcres.toFixed(1); row.otherFormAcres = +row.otherFormAcres.toFixed(1); row.hmcAcres = +row.hmcAcres.toFixed(1);
    row.mappedAcres = +mAc.toFixed(1);
    const share = row.plantedAcres > 0 ? mAc / row.plantedAcres : null;
    row.mappedShare = share == null ? null : +share.toFixed(4);
    row.mappedPct = share == null ? null : Math.round(share * 100);
    if (!clus.size) { row.why = "no field of this unit is on the acreage map, so the machine cannot be placed on it"; continue; }
    if (!(mAc > 0)) { row.status = STATUS.NOT_ENOUGH; row.why = "no combine yield on this unit yet"; continue; }

    const cal = calOf(row.farmerId, crop);
    const factor = cal && cal.factor > 0 ? cal.factor : 1;
    row.calibration = cal ? (cal.ambiguous ? { ambiguous: true } : { factor: cal.factor, source: cal.source }) : null;
    row.machineYieldRaw = +(mQty / mAc).toFixed(1);
    row.machineYield = +((mQty / mAc) * factor).toFixed(1);

    if (share > OVER_COVER_TOL) {
      row.why = `the machine ran ${row.mappedPct}% of this unit's planted acres — more ground than the unit insures, so some of that harvest belongs elsewhere`;
      continue;
    }
    if (share < MIN_MAPPED_SHARE) {
      row.status = STATUS.NOT_ENOUGH;
      row.why = `${row.mappedPct}% of the unit's acres carry a combine yield; ${Math.round(MIN_MAPPED_SHARE * 100)}% is needed`;
      continue;
    }
    row.status = row.machineYield < row.guaranteeYield ? STATUS.BELOW : STATUS.AT_OR_ABOVE;
  }

  units.sort((a, b) => (a.status === STATUS.BELOW ? 0 : 1) - (b.status === STATUS.BELOW ? 0 : 1)
    || String(a.farmerId).localeCompare(String(b.farmerId)) || a.crop.localeCompare(b.crop) || a.unit.localeCompare(b.unit));
  const counts = {};
  for (const s of Object.values(STATUS)) counts[s] = units.filter((u) => u.status === s).length;
  return { ruleVersion: RULE_VERSION, cropYear: CY, minMappedShare: MIN_MAPPED_SHARE, units, counts };
}

const STRUCTURE_WORD = { EU: "enterprise unit", BU: "basic unit", OU: "optional unit" };
const f1 = (v) => (v == null ? "—" : (Math.round(v * 10) / 10).toLocaleString("en-US", { maximumFractionDigits: 1 }));

// The one sentence an agent reads. No dollars.
function describeUnitFlag(u) {
  if (!u) return "";
  const titleCase = (s) => String(s || "").toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
  const where = [u.county ? titleCase(u.county) : null, u.structure ? STRUCTURE_WORD[u.structure] : null].filter(Boolean);
  const head = `Unit ${u.unit} ${cropWord(u.crop)}${where.length ? ` (${where.join(", ")})` : ""}`;
  if (u.status === STATUS.BELOW || u.status === STATUS.AT_OR_ABOVE) {
    const cal = u.calibration && u.calibration.factor ? ", post-calibrated" : ", monitor not calibrated";
    return `${head}: machine yield ${f1(u.machineYield)} ${u.yieldUnit} vs guarantee ${f1(u.guaranteeYield)} ${u.yieldUnit} across ${u.mappedPct}% of the unit's acres${cal}`;
  }
  if (u.status === STATUS.NOT_ENOUGH) {
    // The figures so far, for the agent watching the unit come in — never a flag.
    const soFar = u.machineYield != null && u.guaranteeYield != null
      ? ` (so far ${f1(u.machineYield)} ${u.yieldUnit} vs guarantee ${f1(u.guaranteeYield)} ${u.yieldUnit})` : "";
    return `${head}: not enough harvested yet — ${u.why}${soFar}`;
  }
  return `${head}: not checked — ${u.why}`;
}

// ── WHAT THE RULES ABOVE READ (check-query-contract shape) ──────────────────
const UNIT_GUARANTEE_NEEDS = Object.freeze({
  policies: ["id", "farmer_id", "policy_number", "crop_year"],
  crop_coverage_units: ["policy_id", "crop_year", "county", "crop", "plan", "type_code", "unit_number", "sub_unit",
    "unit_structure", "is_prevent_plant", "acres", "aph_yield", "coverage_level", "acre_guarantee_qty", "acreage_status"],
  policy_fields: ["policy_id", "farmer_id", "crop", "crop_year", "clu_id", "unit_number", "sub_unit", "acres"],
  jd_field_ftf: ["farmer_id", "clu_id", "crop", "metric", "operation_date", "acres", "mean_value", "jd_operation_id"],
  yield_calibrations: ["farmer_id", "crop_year", "crop", "total_bushels", "total_acres", "source"],
  claim_hmc_fields: ["farmer_id", "crop_year", "crop", "field_key"],
});

module.exports = {
  buildUnitGuaranteeFlags, describeUnitFlag, calibrationFor, rowGuaranteePerAcre, machineUnitFor,
  groupCoverageUnits, unitGuaranteePerAcre, pad4,
  STATUS, MIN_MAPPED_SHARE, OVER_COVER_TOL, RULE_VERSION, UNIT_GUARANTEE_NEEDS,
};
